import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PageList from '../../templates/PageList';
import PageSearch from '../../molecules/PageSearch';
import SelectFilter from '../../organisms/SelectFilter';
import { ATableHeader } from '../../organisms/ATable';
import GroupRow from './group/GroupRow';
import communicationApi from '../../../services/communicationApi';
import { Group, MessageComposition } from '../../../types/communication';

const STATUS_OPTIONS = [
    { value: 'ACTIVE', label: 'Ativo' },
    { value: 'INACTIVE', label: 'Inativo' },
];

const TABLE_HEADERS: ATableHeader[] = [
    { id: 'g-name', title: 'Grupo', field: 'name', canSort: true },
    { id: 'g-users', title: 'Membros', field: 'users', canSort: false, width: '180px' },
    { id: 'g-models', title: 'Composições', field: 'models', canSort: false, width: '200px' },
    { id: 'g-status', title: 'Status', field: 'status', canSort: false, width: '90px' },
];

const GroupPageList = () => {
    const navigate = useNavigate();
    const { page = '0', pageSize = '20', field = 'name', order = 'ASC' } = useParams();
    const [rows, setRows] = useState<Group[]>([]);
    const [models, setModels] = useState<MessageComposition[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    const listUrl = `/communication/groups/${page}/${pageSize}/${field}/${order}`;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [groups, metas] = await Promise.all([
                communicationApi.listGroups({ search, status: filterStatus }),
                communicationApi.listMessageCompositions({ pageSize: 9999 }),
            ]);
            const sorted = [...groups].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR') * (order === 'DESC' ? -1 : 1));
            setRows(sorted);
            setModels(metas.list);
        } catch {
            toast.error('Falha ao carregar grupos.');
        } finally {
            setLoading(false);
        }
    }, [search, filterStatus, order]);

    useEffect(() => {
        load();
    }, [load]);

    const groupOutletContext = {
        closeTo: listUrl,
        onChanged: () => load(),
        onGroupCreated: (group: Group) => {
            load();
            navigate(`${listUrl}/item/${group.id}`);
        },
    };

    const renderRows = () => {
        if (!rows.length) {
            return (
                <tr>
                    <td colSpan={TABLE_HEADERS.length} className="py-12 px-2 text-center">
                        <div className="text-sm font-semibold text-gray-600 dark:text-gray-300">Nenhum grupo encontrado</div>
                        <div className="text-xs text-gray-400 mt-1">Grupos reúnem as pessoas que operam um conjunto de composições.</div>
                    </td>
                </tr>
            );
        }
        return rows.map((g, index) => (
            <GroupRow
                key={g.id}
                group={g}
                models={models.filter((m) => m.groupId === g.id)}
                striped={index % 2 === 0}
                onOpen={() => navigate(`${listUrl}/item/${g.id}`)}
            />
        ));
    };

    return (
        <>
            <PageList
                pageTitle="Grupos"
                url=""
                breadcrumb={[
                    { label: 'Comunicação', url: '#' },
                    { label: 'Grupos', url: '#' },
                ]}
                tableFieldSort={field}
                tableOrderSort={order as 'ASC' | 'DESC'}
                onTableSort={(f, o) => navigate(`/communication/groups/0/20/${f}/${o}`, { replace: true })}
                renderRows={renderRows as any}
                page={0}
                pageSize={rows.length || 20}
                onPageChange={() => undefined}
                onItemsPerPageChange={() => undefined}
                itemsLabel="grupos"
                defaultBackendHeader={false}
                tableHeaders={TABLE_HEADERS}
                isLoadingData={loading}
                externalTotalElements={rows.length}
                externalTotalPages={1}
                outletContext={groupOutletContext}
            >
                {{
                    controlSlot: (
                        <div className="flex justify-end">
                            <button
                                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                                onClick={() => navigate('new', { relative: 'path' })}
                            >
                                <FaPlus size={14} /> Novo grupo
                            </button>
                        </div>
                    ),
                    filterSlot: (
                        <div className="grid grid-cols-12 gap-1">
                            <SelectFilter
                                id="g-f-status"
                                label="Status"
                                value={filterStatus}
                                onChange={setFilterStatus}
                                defaultVoidOption
                                defaultLabel="Todos..."
                                options={STATUS_OPTIONS}
                                className="col-span-3"
                            />
                            <PageSearch
                                value={search}
                                label="Procurar por nome..."
                                onSearch={setSearch}
                                filterCleanVisible
                                filterCleanEnabled={Boolean(search || filterStatus)}
                                onFilterClean={() => {
                                    setSearch('');
                                    setFilterStatus('');
                                }}
                                configureVisible={false}
                                className="col-span-9"
                            />
                        </div>
                    ),
                }}
            </PageList>
        </>
    );
};

export default GroupPageList;
